import { Network, Database, ShieldCheck, Search } from "lucide-react";

const CAPS = [
  { icon: Network, title: "Context Generation", desc: "Automatically discover and map the relationships and meaning across enterprise data, processes and knowledge." },
  { icon: Database, title: "Context Persistence", desc: "Store adaptive context across knowledge graphs, vector stores and enterprise memory over time." },
  { icon: ShieldCheck, title: "Context Validation", desc: "Continuously check context for completeness, consistency, conflicts and freshness." },
  { icon: Search, title: "Context Retrieval", desc: "Rank and assemble the most relevant context for every query, agent and task." },
];

const Grid = ({ children }) => <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>;

/* 1 · Warm cream card + caramel icon chip */
const V1 = () => (
  <Grid>
    {CAPS.map((c) => (
      <div key={c.title} className="rounded-xl bg-[#F5EEE1] border border-[#8A5730]/12 p-4 hover:border-[#8A5730]/30 transition-colors duration-300">
        <div className="w-9 h-9 rounded-lg bg-[#8A5730]/12 text-[#8A5730] flex items-center justify-center mb-3"><c.icon size={16} strokeWidth={1.9} /></div>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
        <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
      </div>
    ))}
  </Grid>
);

/* 2 · Translucent glass card on the oat bg */
const V2 = () => (
  <Grid>
    {CAPS.map((c) => (
      <div key={c.title} className="rounded-xl bg-white/45 backdrop-blur-sm border border-white/60 p-4 hover:bg-white/60 transition-colors duration-300">
        <div className="w-9 h-9 rounded-lg bg-[#211307]/8 text-[#3E2818] flex items-center justify-center mb-3"><c.icon size={16} strokeWidth={1.9} /></div>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
        <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
      </div>
    ))}
  </Grid>
);

/* 3 · Espresso-tinted card, gold icon, top-accent */
const V3 = () => (
  <Grid>
    {CAPS.map((c) => (
      <div key={c.title} className="rounded-xl bg-[#EAE0CE] border border-[#3E2818]/10 p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-lg bg-[#211307] text-[#E8AA68] flex items-center justify-center shrink-0"><c.icon size={16} strokeWidth={1.9} /></div>
        </div>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
        <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
      </div>
    ))}
  </Grid>
);

/* 4 · Open — no card, icon over hairline */
const V4 = () => (
  <Grid>
    {CAPS.map((c) => (
      <div key={c.title} className="border-t-2 border-[#8A5730]/25 pt-4">
        <div className="w-10 h-10 rounded-full bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><c.icon size={17} strokeWidth={1.9} /></div>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
        <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
      </div>
    ))}
  </Grid>
);

/* 5 · Icon-left row cards, warm bg */
const V5 = () => (
  <Grid>
    {CAPS.map((c) => (
      <div key={c.title} className="rounded-xl bg-[#F3ECDE] border border-[#8A5730]/12 p-4">
        <div className="w-9 h-9 rounded-lg bg-white text-[#8A5730] border border-[#8A5730]/15 flex items-center justify-center mb-3"><c.icon size={16} strokeWidth={1.9} /></div>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
        <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
      </div>
    ))}
  </Grid>
);

const OPTIONS = [
  { id: "1", title: "Style 1 · Warm Cream Card", note: "Soft cream fill (less white) with a caramel icon chip. Warm and cohesive with the section background.", comp: V1 },
  { id: "2", title: "Style 2 · Frosted Glass", note: "Translucent white cards that let the oat background show through. Light and airy.", comp: V2 },
  { id: "3", title: "Style 3 · Espresso Icon on Oat", note: "Deeper oat card with a solid espresso icon tile (gold glyph) for a premium, high-contrast accent.", comp: V3 },
  { id: "4", title: "Style 4 · Open, No Card", note: "No box at all — a round icon over a caramel hairline. Minimal and editorial; the background stays fully visible.", comp: V4 },
  { id: "5", title: "Style 5 · Warm Card + White Icon Tile", note: "Warm card with a clean white icon tile. Subtle depth without heavy white boxes.", comp: V5 },
];

export default function CapabilityCardOptions() {
  return (
    <div className="min-h-screen bg-[#EFE7D9]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Enterprise Brain · Capability Box Styles</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a style for the 4 boxes</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Trimmed copy + icons, shown on the Section 5 background. Tell me the number.</p>

        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-4">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
